import { autoUpdate, flip, offset, size, useFloating } from "@floating-ui/react"
import clsx from "clsx"
import { HTMLAttributes, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { IOption } from "src/types"
import { ClickAwayListener } from "../../ClickAway"
import { SelectControl, SelectControlProps } from "./components/Control"
import "./style.scss"

export interface ISelectorProps<T = string, Meta extends object = object>
  extends Omit<SelectControlProps<T, Meta>, "value" | "onChange" | "reference"> {
  options: IOption<T, Meta>[]
  value?: IOption<T, Meta> | T
  onChange?: (value: IOption<T, Meta>) => void
  classNames?: Partial<{
    list: string
  }>
  valueGetter?: (option: IOption<T, Meta> | string) => string
  renderOptions?: (option: IOption<T, Meta>, props: HTMLAttributes<HTMLDivElement> & { key: any }, index: number) => ReactNode
}

export const Selector = <T extends string | number = string, Meta extends object = object>({
  renderOptions,
  value,
  options,
  onChange,
  classNames,
  valueGetter,
  onInput,
  ...controlProps
}: ISelectorProps<T, Meta>) => {
  const [open, setOpen] = useState(false)
  const listWrapper = useRef<HTMLDivElement>(null)
  const selectedIndex = useRef(-1)
  const [filteredOptions, setFilteredOptions] = useState<IOption<T, Meta>[]>(options)
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    open: open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight, 500)}px`,
            minWidth: `${rects.reference.width}px`,
          })
        },
        padding: 10,
      }),
    ],
  })
  const close = () => setOpen(false)
  const selectValue = useMemo(() => {
    if (typeof value === "object") return options?.find((option) => option === value) ?? value
    return options?.find((option) => option.value === value)
  }, [options, value])

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  const scrollToSelected = (navigateIndex: number) => {
    if (!open) {
      setOpen(true)
      return
    }
    const el = listWrapper.current?.querySelector(`#select-option-${selectedIndex.current}`)
    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "start" })
      el.classList.remove("select-option__active")
    }
    let index = selectedIndex.current + navigateIndex
    if (index < 0) index = filteredOptions.length - 1
    else if (index >= filteredOptions.length) index = 0
    const element = listWrapper.current.querySelector(`#select-option-${index}`)
    if (element != null) {
      element.classList.remove("select-option__active")
      element.scrollIntoView({ block: "nearest", inline: "start" })
      element.classList.add("select-option__active")
      selectedIndex.current = index
    }
  }
  return (
    <ClickAwayListener onClickAway={close}>
      <SelectControl
        {...controlProps}
        reference={refs.setReference}
        onClick={() => setOpen(true)}
        value={valueGetter?.(typeof value === "object" ? value : String(value)) ?? selectValue ?? ""}
        onNavigate={scrollToSelected}
        onEnter={() => {
          onChange?.(filteredOptions[Math.max(0, selectedIndex.current)])
          setOpen(false)
        }}
        onInput={(val, e) => {
          setFilteredOptions(options.filter((option) => option.label.toLowerCase().includes(val.toLowerCase())))
          onInput?.(val, e)
        }}
      />
      {open && (
        <div ref={refs.setFloating} style={floatingStyles} className={clsx("select-custom__list", classNames?.list)}>
          <div className="select-custom__list-wrapper" ref={listWrapper} onMouseDown={(e) => e.preventDefault()}>
            {filteredOptions.map((option, index) => {
              const isSelected = option.value === selectValue?.value
              const props = {
                className: clsx("select-custom__list-item", "min-w-[120px]", {
                  "select-custom__list-selected": isSelected,
                }),
                id: `select-option-${index}`,
                key: index,
                onClick: () => {
                  onChange?.(option)
                  close()
                },
              }
              if (renderOptions != null && typeof renderOptions === "function") return renderOptions(option, props, index)
              return <div {...props}>{option.label}</div>
            })}
          </div>
        </div>
      )}
    </ClickAwayListener>
  )
}
